import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, type ButtonProps, Collapse } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

type Props = {
  expandedByDefault?: boolean;
  nbLinesToShow?: number;
  clipType?: 'dots' | 'gradient';
  toggleSlotProps?: ButtonProps;
} & React.PropsWithChildren;

export const CollapsibleContent: React.FC<Props> = ({
  children,
  nbLinesToShow,
  expandedByDefault = false,
  clipType = 'gradient',
  toggleSlotProps,
}) => {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(expandedByDefault);
  const [collapsedHeight, setCollapsedHeight] = useState<number>(0);
  const [fullHeight, setFullHeight] = useState<number>(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const shouldShowToggle = useMemo(
    () => fullHeight > collapsedHeight && collapsedHeight > 0,
    [fullHeight, collapsedHeight],
  );

  useEffect(() => {
    if (rootRef.current && nbLinesToShow != null) {
      const currentFullHeight = rootRef.current.scrollHeight;
      setFullHeight(currentFullHeight);

      // Temporarily apply styles to measure clamped height
      const originalStyles = {
        overflow: rootRef.current.style.overflow,
        textOverflow: rootRef.current.style.textOverflow,
        display: rootRef.current.style.display,
        webkitLineClamp: rootRef.current.style.webkitLineClamp,
        webkitBoxOrient: rootRef.current.style.webkitBoxOrient,
      };

      // Apply clamping styles
      rootRef.current.style.overflow = 'hidden';
      rootRef.current.style.textOverflow = 'ellipsis';
      rootRef.current.style.display = '-webkit-box';
      rootRef.current.style.webkitLineClamp = nbLinesToShow.toString();
      rootRef.current.style.webkitBoxOrient = 'vertical';

      // Force layout recalculation
      const clampedHeight = rootRef.current.clientHeight;

      // Restore original styles
      rootRef.current.style.overflow = originalStyles.overflow;
      rootRef.current.style.textOverflow = originalStyles.textOverflow;
      rootRef.current.style.display = originalStyles.display;
      rootRef.current.style.webkitLineClamp = originalStyles.webkitLineClamp;
      rootRef.current.style.webkitBoxOrient = originalStyles.webkitBoxOrient;

      setCollapsedHeight(clampedHeight);
    }
  }, [rootRef, nbLinesToShow, children]);

  return (
    <Box>
      <Collapse
        in={expanded}
        collapsedSize={collapsedHeight}
        sx={theme => ({
          position: 'relative',
          ':after':
            shouldShowToggle && clipType === 'gradient'
              ? {
                  opacity: expanded ? 0 : 1,
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 30,
                  background:
                    'linear-gradient(0deg, rgba(255,255,255,1) 15%, rgba(255,255,255,0.5) 60%, rgba(255,255,255,0) 100%)',
                  zIndex: 1,
                  transition: 'opacity 0.3s',
                  ...theme.applyStyles('dark', {
                    background:
                      'linear-gradient(0deg, rgba(30,34,42,1) 15%, rgba(30,34,42,0.5) 60%, rgba(30,34,42,0) 100%)',
                  }),
                }
              : {},
        })}
      >
        <Box
          ref={rootRef}
          id="collapsible-content-region"
          sx={{
            ...(clipType === 'dots'
              ? {
                  lineClamp: expanded ? undefined : nbLinesToShow,
                  WebkitLineClamp: expanded ? undefined : nbLinesToShow,
                  overflow: 'hidden',
                }
              : {}),
          }}
        >
          {children}
        </Box>
      </Collapse>
      {shouldShowToggle && (
        <Button
          sx={{ mt: 0.5 }}
          color="primary"
          variant="text"
          aria-expanded={expanded}
          aria-controls="collapsible-content-region"
          endIcon={
            expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />
          }
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          {...(toggleSlotProps ?? {})}
        >
          {expanded ? t('Show less') : t('Show more')}
        </Button>
      )}
    </Box>
  );
};
